/**
 * Module 5: prices, history and approval (2 minutes). Where a price comes from and the
 * quotation history in the Details of a row, the price band, the Check button and who
 * approves a held price.
 *
 * Figures match practice/facts.ts: PRACTICE-0001 line 1 (list price 215.00 EUR, last
 * quoted SQ-demo-0042 at 209.00 EUR, Won); PRACTICE-0005 Nordcast GmbH (Price approval
 * needed, more than 10 points below; manager sees cost 180.00, 0.21 against 0.35).
 * Round 3 (12 Oct 2026): Simplified Technical English.
 */

import type { Module } from "@/features/training/types";

import { checkSlide } from "../check-slide";

export const MODULE: Module = {
  id: "prices",
  title: "Prices, history and approval",
  menuTitle: "Prices",
  minutes: 2,
  kind: "lesson",
  goals: [
    "Read where a price comes from and what the last quotation said.",
    "Know the price band and who approves a price.",
  ],
  helpTopics: ["case-page"],
  slides: [
    {
      id: "p1",
      tone: "light",
      title: "When you need to know where a price comes from",
      job: "When you need to know where a price comes from, open the details of the row.",
      path: ["Case", "Draft", "Line 1", "Details"],
      body: [
        {
          kind: "text",
          text: "The `Price` column shows the price. Its source and its history sit behind the chevron, in the `Details` of the row. For the blade on this case:",
        },
        {
          kind: "list",
          items: [
            "**List price 215.00 EUR per 1 pcs (ROW-100)**: the price list the price comes from.",
            "**Last quoted SQ-demo-0042: 8 pcs at 209.00 EUR, Won**: the last quotation of this item to this customer, and its result.",
            "**Quoted 3 times in the last 18 months**: the number of quotations of this item.",
          ],
        },
      ],
      interaction: {
        id: "p1-history",
        kind: "hotspots",
        required: true,
        minOpen: 1,
        stage: { view: "case", caseId: "PRACTICE-0001", part: "prices" },
        spots: [
          {
            anchor: "case-history",
            label: "Price and history",
            text: "The list price, the last quotation and its result. A won quotation at 209.00 EUR is a good reference.",
          },
        ],
      },
      check: "Compare the price with the last quotation. A big gap needs a reason.",
      labels: ["List price", "Last quoted", "in the last 18 months", "Details"],
    },
    {
      id: "p2",
      tone: "grey",
      title: "Price approval needed: your manager approves",
      job: "Confirm the line and tell your manager when a line says Price approval needed.",
      body: [
        {
          kind: "text",
          text: "A price below the margin limit holds the case. The big button says `Check` with the reason, `Price approval needed`. The sentence next to it says: the line is held, the price is below the limit. The button goes to the held row. A line with no price says `Held: no price`.",
        },
        {
          kind: "text",
          text: "Sales people see a band, not the figures. The row carries a warning triangle. Its `Details` say `Below the limit by` up to 5 points, by 5 to 10 points, or by more than 10 points. On this case for Nordcast GmbH it is more than 10 points below.",
        },
        {
          kind: "steps",
          steps: [
            "Decide the rows as usual.",
            "Tell your manager about the case.",
            "Your manager presses `Approve price`: on the big button, in the row menu of the line, or under `More`. One click, and the line is not held any more.",
            "The approval stays until the item, the price, the quantity or the customer of that line changes. The page then says so, and your manager approves again.",
          ],
        },
      ],
      interaction: {
        id: "p2-roles",
        kind: "before-after",
        required: false,
        tabs: [
          {
            id: "p2-sales",
            label: "What sales people see",
            stage: { view: "case", caseId: "PRACTICE-0005", part: "prices", role: "sales" },
          },
          {
            id: "p2-manager",
            label: "What your manager sees",
            stage: { view: "case", caseId: "PRACTICE-0005", part: "prices", role: "manager" },
            blocks: [
              {
                kind: "text",
                text: "Your manager sees the cost (180.00), the contribution (0.21) and the limit (0.35). Your manager approves after you confirm the line.",
              },
            ],
          },
        ],
      },
      check:
        "Approve price is on the big button after you decide the rows. Decide first, then ask.",
      labels: [
        "Price approval needed",
        "Held: no price",
        "Check",
        "Approve price",
        "Below the limit by",
      ],
    },
    {
      id: "p3",
      tone: "light",
      title: "Quotations for this inquiry exist",
      job: "Decide what to reuse when a case says `Quotations for this inquiry exist`.",
      body: [
        {
          kind: "text",
          text: "Some cases say `Quotations for this inquiry exist` in the `Details` sheet. The big button then reads `Nothing to do` with the quotation number. A colleague may have quoted it already. The header defaults then come from the last quotation of the customer.",
        },
      ],
      interaction: {
        id: "p3-sort",
        kind: "sort",
        required: true,
        prompt: "Reuse it as it is, or check it first?",
        bins: ["Reuse the defaults", "Check first"],
        cards: [
          {
            id: "p3-terms",
            text: "Payment terms from the last quotation",
            bin: 0,
            why: "Terms rarely change between two quotations to the same customer.",
          },
          {
            id: "p3-currency",
            text: "Currency from the last quotation",
            bin: 0,
            why: "The customer usually buys in the same currency.",
          },
          {
            id: "p3-existing",
            text: "A quotation for this inquiry already exists",
            bin: 1,
            why: "Somebody may have replied already. Check before you quote it twice.",
          },
          {
            id: "p3-delivery",
            text: "A delivery date read from the mail",
            bin: 1,
            why: "I2Q sometimes reads the deadline of the inquiry as the delivery date. Compare with the mail.",
          },
        ],
      },
      check:
        "An existing quotation for the same inquiry means: find out who sent it before you quote again.",
      labels: ["Quotations for this inquiry exist", "Nothing to do"],
    },
    {
      id: "p4",
      tone: "grey",
      title: "Check: a held price",
      job: "Click the right option to finish the module.",
      interaction: {
        id: "p4-check",
        kind: "check",
        required: true,
        question: {
          id: "p4-q",
          topic: "prices",
          stem: "The big button says `Check: Price approval needed`. What next?",
          options: [
            {
              id: "p4-q-a",
              text: "Send the quotation with that price anyway.",
              correct: false,
              fb: "No. The price is below the limit. Your manager must approve it first.",
            },
            {
              id: "p4-q-b",
              text: "Mark the case Not for us.",
              correct: false,
              fb: "No. The inquiry is real. Only the price needs an approval.",
            },
            {
              id: "p4-q-c",
              text: "Decide the rows. Your manager presses `Approve price` under `More`.",
              correct: true,
              fb: "Right. Decide the rows first. Then your manager presses `Approve price`: on the big button, in the row menu of the line, or under `More`.",
            },
            {
              id: "p4-q-d",
              text: "Give feedback and wait.",
              correct: false,
              fb: "No. Feedback goes to the build team. Your manager approves the price.",
            },
          ],
        },
      },
      check: "Price approval needed: decide the rows, then tell your manager.",
      labels: ["Price approval needed", "Approve price"],
    },
    checkSlide({
      id: "p5",
      tone: "light",
      title: "Check: price source",
      topic: "prices",
      stem: "Where does `List price 215.00 EUR per 1 pcs (ROW-100)` come from?",
      answers: [
        [
          "From the last quotation.",
          false,
          "No. The last quotation has its own line, Last quoted.",
        ],
        ["From the price list ROW-100.", true, "Right. The name in brackets is the price list."],
        [
          "From the customer's mail.",
          false,
          "No. The customer asked for a price. The price comes from the list.",
        ],
        ["I2Q made it up.", false, "No. Every price shows its source."],
      ],
      check: "Every price shows where it comes from. Read it.",
    }),
    checkSlide({
      id: "p6",
      tone: "grey",
      title: "Check: an earlier quotation",
      topic: "prices",
      stem: "The case says `Quotations for this inquiry exist`. What do you do first?",
      answers: [
        ["Quote it again with fresh prices.", false, "No. The customer may already have a reply."],
        [
          "Find out who sent the earlier quotation.",
          true,
          "Right. Then you know whether it still needs you.",
        ],
        ["Mark the case Not for us.", false, "No. Check first. It may still need a reply."],
        ["Ignore the note.", false, "No. The note is there to stop a second quotation."],
      ],
      check: "One inquiry, one quotation. Check before you quote again.",
      labels: ["Quotations for this inquiry exist"],
    }),
  ],
};
